/**
 * `ProgressStore`, persistence of the learning-journey progression.
 *
 * Pure TypeScript: no React, no Studio, no DOM imports. The browser
 * `localStorage` is reached through a tiny `StorageLike` indirection so
 * the store can be unit-tested under `testEnvironment: 'node'` (Jest)
 * without monkey-patching `globalThis.localStorage`.
 *
 * ## Schema
 *
 *   - `schemaVersion: 2`       , bumped when the JSON shape changes.
 *   - `phrasePlanHash: string` , hash of the deterministic phrase plan
 *                                 (cf. `phrase-plan.ts#hashPhrasePlan`).
 *
 * ## Invalidation rules (centralized inside `load`)
 *
 *   - JSON parse error                       → return `null`.
 *   - `schemaVersion !== 2`                  → return `null`.
 *   - Stored `phrasePlanHash` ≠ expected     → return `null`.
 *
 * The expected hash is passed to `load(morceauId, expectedHash)` so the
 * caller does not have to know the schema details, the store is the
 * single point of truth for "is this save still valid". Callers that
 * receive `null` know they must reconstruct from scratch.
 *
 * ## `load` never deletes
 *
 * A read must not destroy. Earlier revisions cleared the entry on every
 * invalidation path, which turned a *transposition* into silent, final
 * data loss: transposing keeps the same `morceauId` but shifts every
 * pitch, so the plan hash changes and the save was wiped, even though
 * transposing back re-derives the original hash and would have
 * rehydrated perfectly.
 *
 * We now leave the stale payload in place. It costs ~2 kB per piece and
 * buys back two properties:
 *
 *   - an invalidation is recoverable whenever the original plan can be
 *     re-derived (undo a transposition, roll back an analyzer change);
 *   - a payload written by a *newer* schema is never destroyed by an
 *     older bundle still running in another tab.
 *
 * Erasure is only ever explicit, through `clear`.
 *
 * See spec §"Persistance de la progression".
 */

import type { Phase } from './phase-fsm';
import type { PhraseState } from './types';

// ── Schema ────────────────────────────────────────────────────────

/**
 * Per-phrase persisted progression. Strict subset of what the FSM
 * exposes, we only persist what is needed to rehydrate the next
 * session, never the run history or transient counters.
 *
 * `etatMax` carries the highest `PhraseState` reached, plus the
 * sentinel `'DONE'` for fully-validated phrases.
 *
 * `tempoValideMax` is a fraction of the target tempo (e.g. `0.6`,
 * `0.75`, `0.9`, `1.0`). `0` means "never validated".
 */
export type PhraseProgress = {
  readonly id: string;
  readonly etatMax: PhraseState;
  readonly tempoValideMax: number;
  /** Epoch ms of the last time this unit was played through. `0` = never. */
  readonly lastPlayedAt: number;
};

/** What one cell of the piece's rail looked like after the last traversal. */
export type SectionProgress = {
  readonly id: string;
  /** Share of the cell's attacks that were answered, in `[0, 1]`. */
  readonly honoured: number;
  readonly stopped: boolean;
};

/**
 * The last time the piece was crossed, and what that crossing saw.
 *
 * Persisted because phase 2 is a project of several sessions — the tempo
 * climbs **9 rungs in the median** — and a player coming back tomorrow was
 * shown an empty rail, as if nothing had ever been played.
 */
export type TraversalProgress = {
  /** Epoch ms. */
  readonly at: number;
  readonly sections: readonly SectionProgress[];
  /**
   * The tempo it was played at, as a factor of the written one.
   *
   * Without it the honoured shares below are unreadable: 0.95 at 0.35 and
   * 0.95 at 1 are not the same event, and the line that greets a returning
   * player quotes a BPM — it must quote the one they actually played.
   */
  readonly rate: number;
  /**
   * Whether that crossing held, as it was judged **on the day**.
   *
   * Recomputing it would let a later change of threshold rewrite what the
   * player was already told.
   */
  readonly held: boolean;
};

export type JourneyProgress = {
  readonly schemaVersion: 2;
  readonly phrasePlanHash: string;
  readonly morceauId: string;
  readonly phrases: readonly PhraseProgress[];
  readonly phaseGlobale: Phase;
  readonly tempoSpeedupCourant: number | null;
  /** The last crossing of the whole piece, or `null` if there was none. */
  readonly lastTraversal: TraversalProgress | null;
};

export const PROGRESS_SCHEMA_VERSION = 2 as const;

// ── Public interface ──────────────────────────────────────────────

/**
 * Read/write/clear contract for journey progression. Implemented by
 * `LocalStorageProgressStore` (production) and `MemoryProgressStore`
 * (tests). The orchestrator depends only on this interface.
 */
export interface ProgressStore {
  /**
   * Returns the persisted `JourneyProgress` for `morceauId`, or `null`
   * when nothing valid is stored. Validation (schema version + plan
   * hash) is the store's responsibility, `null` always means "build a
   * fresh state".
   *
   * Never mutates the backing storage: an invalidated entry is kept, so
   * a plan that becomes derivable again rehydrates from it.
   */
  load(morceauId: string, expectedPhrasePlanHash: string): JourneyProgress | null;

  /**
   * Persists `progress` for `morceauId`. Overwrites any existing entry.
   *
   * Best-effort: a storage failure (quota exhausted, private-mode
   * restrictions) is swallowed rather than thrown. Persistence is a
   * side-channel of the journey, losing a write must never abort a run
   * in progress.
   */
  save(morceauId: string, progress: JourneyProgress): void;

  /** Removes the entry for `morceauId`, no-op if absent. */
  clear(morceauId: string): void;
}

// ── Storage indirection ───────────────────────────────────────────

/**
 * Minimal subset of the DOM `Storage` interface used by the
 * `LocalStorageProgressStore`. Lets tests inject a Map-backed mock
 * without depending on jsdom.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// ── Storage key ───────────────────────────────────────────────────

/**
 * Exporté pour la même raison que `journeyStorageKey`, et pour un lecteur de
 * plus : la fermeture de compte doit balayer **toutes** les progressions du
 * navigateur sans en connaître les identifiants, ce qu'une clé unitaire ne
 * permet pas (`lib/account/local-purge.ts`).
 */
export const JOURNEY_KEY_PREFIX = 'virtuosa:journey:';

const KEY_PREFIX = JOURNEY_KEY_PREFIX;

/**
 * Clé de rangement d'une progression.
 *
 * Exportée parce qu'un second lecteur existe désormais hors du parcours : la
 * carte de « Mes morceaux » lit la progression pour afficher où l'on en est
 * (`lib/library/journey-glance.ts`). Il lui faut la clé, et recopier le
 * préfixe ailleurs, c'est accepter que les deux divergent un jour.
 */
export function journeyStorageKey(morceauId: string): string {
  return `${KEY_PREFIX}${morceauId}`;
}

// ── LocalStorageProgressStore ─────────────────────────────────────

/**
 * Persists the journey progression in a `Storage` (typically
 * `window.localStorage`). The storage backend is injected to keep the
 * class testable in a Node environment.
 *
 * If no storage is provided and `globalThis.localStorage` is not
 * available either, the constructor throws, failing loud is preferable
 * to silently dropping every save.
 */
export class LocalStorageProgressStore implements ProgressStore {
  private readonly storage: StorageLike;

  /** Guards the one-shot console warning emitted by `save`. */
  private persistFailureWarned = false;

  constructor(storage?: StorageLike) {
    const resolved = storage ?? resolveDefaultStorage();

    if (resolved === null) {
      throw new Error(
        'LocalStorageProgressStore: no Storage available. ' +
          'Provide one explicitly when constructing the store, or run in ' +
          'a context where `globalThis.localStorage` exists.',
      );
    }

    this.storage = resolved;
  }

  load(morceauId: string, expectedPhrasePlanHash: string): JourneyProgress | null {
    const raw = this.storage.getItem(journeyStorageKey(morceauId));

    if (raw === null) {
      return null;
    }

    const parsed = parseProgress(raw);

    if (parsed === null) {
      return null;
    }

    if (parsed.schemaVersion !== PROGRESS_SCHEMA_VERSION) {
      return null;
    }

    if (parsed.phrasePlanHash !== expectedPhrasePlanHash) {
      return null;
    }

    return parsed;
  }

  save(morceauId: string, progress: JourneyProgress): void {
    const key = journeyStorageKey(morceauId);

    try {
      this.storage.setItem(key, JSON.stringify(progress));
    } catch (error) {
      // Quota exhausted (the journey payload is small, but it shares the
      // 5 MB origin budget with heavier neighbours such as
      // `virtuosa:generation-history`) or storage denied outright.
      // Warn once per store instance: a warning per validated phrase
      // would drown the console without adding information.
      if (!this.persistFailureWarned) {
        this.persistFailureWarned = true;
        console.warn(
          `LocalStorageProgressStore: could not persist "${key}". ` +
            'Progression for this session will not survive a reload.',
          error,
        );
      }
    }
  }

  clear(morceauId: string): void {
    this.storage.removeItem(journeyStorageKey(morceauId));
  }
}

// ── MemoryProgressStore ───────────────────────────────────────────

/**
 * In-memory `ProgressStore`. Trivial Map-backed implementation, used
 * by unit tests that exercise the orchestrator without bringing the
 * full `LocalStorageProgressStore` indirection.
 */
export class MemoryProgressStore implements ProgressStore {
  private readonly entries = new Map<string, JourneyProgress>();

  load(morceauId: string, expectedPhrasePlanHash: string): JourneyProgress | null {
    const stored = this.entries.get(morceauId);

    if (stored === undefined) {
      return null;
    }

    if (stored.schemaVersion !== PROGRESS_SCHEMA_VERSION) {
      return null;
    }

    if (stored.phrasePlanHash !== expectedPhrasePlanHash) {
      return null;
    }

    return stored;
  }

  save(morceauId: string, progress: JourneyProgress): void {
    this.entries.set(morceauId, progress);
  }

  clear(morceauId: string): void {
    this.entries.delete(morceauId);
  }
}

// ── Internals ─────────────────────────────────────────────────────

/**
 * Resolves the default storage backend at construction time. Returns
 * `null` when neither path is available, the caller throws in that
 * case (rather than silently swallowing every persistence call).
 */
function resolveDefaultStorage(): StorageLike | null {
  if (typeof globalThis === 'undefined') {
    return null;
  }

  const ls = (globalThis as { localStorage?: StorageLike }).localStorage;

  if (ls === undefined || ls === null) {
    return null;
  }

  return ls;
}

/**
 * Parses a stored string into a `JourneyProgress`. Returns `null` when:
 *
 *   - the JSON is malformed,
 *   - the parsed shape is not a plain object,
 *   - any required field is missing or has the wrong type.
 *
 * We do not try to fix up partial data: an inconsistent payload from a
 * previous version is safer to ignore than to half-rehydrate. Ignoring
 * is not erasing, the caller keeps the raw entry (see `load`).
 */
function parseProgress(raw: string): JourneyProgress | null {
  let value: unknown;

  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isPlainObject(value)) {
    return null;
  }

  const fields: Record<string, unknown> = {
    ...value,
    phaseGlobale: readPhase(value.phaseGlobale),
  };
  delete fields.introHarmonySkippedKeys;

  const candidate = fields as Partial<JourneyProgress>;

  if (
    typeof candidate.schemaVersion !== 'number' ||
    typeof candidate.phrasePlanHash !== 'string' ||
    typeof candidate.morceauId !== 'string' ||
    !Array.isArray(candidate.phrases) ||
    !isPhase(candidate.phaseGlobale)
  ) {
    return null;
  }

  if (
    candidate.tempoSpeedupCourant !== null &&
    typeof candidate.tempoSpeedupCourant !== 'number'
  ) {
    return null;
  }

  for (const phrase of candidate.phrases) {
    if (!isValidPhraseProgress(phrase)) {
      return null;
    }
  }

  if (!isValidTraversal(candidate.lastTraversal)) {
    return null;
  }

  return candidate as JourneyProgress;
}

/**
 * The persisted phase, checked against the union rather than against
 * `typeof === 'string'`.
 *
 * A payload written by a newer bundle is deliberately kept rather than
 * erased, so an unknown phase name can reach this code. Casting it through
 * left `applyPersistedPhase` with nothing to match: it returned at its first
 * guard and the journey stayed on its opening phase with every unit already
 * `DONE` — a piece that is finished, with no way forward, because the
 * auto-advance only runs on a dispatch to the current unit and there is no
 * current unit.
 */
const PHASES: readonly string[] = ['phase1', 'phase2', 'repertoire'];

function isPhase(value: unknown): value is Phase {
  return typeof value === 'string' && PHASES.includes(value);
}

/**
 * The persisted phase, with the one name the machine no longer has.
 *
 * `intro` was the optional harmonic intro that opened the journey. It is gone
 * for good, and a player who stopped there has done nothing yet: reading it as
 * `phase1` is exactly where a fresh journey opens, and it keeps what the rest
 * of the payload holds. No schema bump for it — refusing the payload would
 * throw away every other field for one name. `introHarmonySkippedKeys`, the
 * list of keys whose intro was skipped, is dropped the same way in
 * `parseProgress`.
 */
function readPhase(value: unknown): unknown {
  if (value === 'intro') {
    return 'phase1';
  }

  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidPhraseProgress(value: unknown): value is PhraseProgress {
  if (!isPlainObject(value)) {
    return false;
  }

  const v = value as Partial<PhraseProgress>;

  return (
    typeof v.id === 'string' &&
    typeof v.etatMax === 'string' &&
    typeof v.tempoValideMax === 'number' &&
    typeof v.lastPlayedAt === 'number'
  );
}

/** `null` is a legitimate value: a piece that has never been crossed. */
function isValidTraversal(value: unknown): value is TraversalProgress | null {
  if (value === null) {
    return true;
  }

  if (
    !isPlainObject(value) ||
    typeof value.at !== 'number' ||
    typeof value.rate !== 'number' ||
    typeof value.held !== 'boolean'
  ) {
    return false;
  }

  if (!Array.isArray(value.sections)) {
    return false;
  }

  return value.sections.every(
    (entry) =>
      isPlainObject(entry) &&
      typeof entry.id === 'string' &&
      typeof entry.honoured === 'number' &&
      typeof entry.stopped === 'boolean',
  );
}
